import { CBSocketClient, SocketClient } from "cbcore-ts"
import { IS, NO, UIViewBroadcastEvent, YES } from "uicore-ts"
import {
    RHCompanyAffiliation,
    RHCompanyMembershipWorkflow,
    RHCompanyRole
} from "../SocketClientFunctions"
import { RHCore } from "./RHCore"
import { RHFeatureFlags } from "./RHFeatureFlags"
import { RHSubscriptionBase } from "./RHSubscriptionBase"
import { referenceId } from "./RHReference"


export type RHCompanyAffiliationStoreChangeReason =
    "affiliations" |
    "membershipWorkflows" |
    "activeCompany" |
    "defaultAllCompaniesScope"

export type RHCompanyAffiliationLoadState =
    "notLoaded" |
    "loading" |
    "loaded" |
    "failed"


export { referenceId }


export function companyIdForAffiliation(affiliation?: RHCompanyAffiliation): string | undefined {
    if (!affiliation?.company) {
        return undefined
    }
    return referenceId(affiliation.company._id)
}


export function directCreateAffiliationForCompany(
    affiliations: RHCompanyAffiliation[],
    companyId: string
): RHCompanyAffiliation | undefined {
    return affiliations.find(affiliation =>
        companyIdForAffiliation(affiliation) === companyId &&
        affiliation.source !== "relationship" &&
        affiliation.membershipStatus === "active" &&
        affiliation.canCreateProcurements
    )
}


export function activeCompanyMatches(
    activeCompanyId: string | undefined,
    activeAffiliation: RHCompanyAffiliation | undefined,
    companyId: string
): boolean {
    return activeCompanyId === companyId &&
        companyIdForAffiliation(activeAffiliation) === companyId &&
        activeAffiliation?.source !== "relationship" &&
        activeAffiliation?.membershipStatus === "active" &&
        activeAffiliation?.canCreateProcurements
}


export class RHCompanyAffiliationStore extends RHSubscriptionBase<
    (reason: RHCompanyAffiliationStoreChangeReason) => void
> {

    _core: RHCore
    _affiliations: RHCompanyAffiliation[] = []
    _membershipWorkflows: RHCompanyMembershipWorkflow[] = []
    _affiliationsPromise?: Promise<RHCompanyAffiliation[]>
    _activeCompanyId?: string
    _directCompanyAffiliation?: RHCompanyAffiliation
    _affiliationLoadState: RHCompanyAffiliationLoadState = "notLoaded"
    _reloadRequestedWhileLoading = NO
    _loadGeneration = 0
    _pushRefreshRequested = NO
    _pushRefreshPromise?: Promise<void>


    constructor(core: RHCore) {
        super()
        this._core = core
        this._registerSocketListeners()
        this._core._functionsToCallForEachSocketClient.push(() => {
            this._registerSocketListeners()
        })
    }


    get affiliations(): RHCompanyAffiliation[] {
        return [...this._affiliations]
    }


    get activeCompanyId(): string | undefined {
        return this._activeCompanyId
    }


    get membershipWorkflows(): RHCompanyMembershipWorkflow[] {
        return [...this._membershipWorkflows]
    }


    get activeCompanyAffiliation(): RHCompanyAffiliation | undefined {
        return this._affiliations.find(affiliation =>
            companyIdForAffiliation(affiliation) === this._activeCompanyId
        )
    }


    get directCompanyAffiliation(): RHCompanyAffiliation | undefined {
        return this._directCompanyAffiliation
    }


    get affiliationLoadState(): RHCompanyAffiliationLoadState {
        return this._affiliationLoadState
    }


    get hasMultipleCompanyAffiliations(): boolean {
        return this._affiliations.filter(affiliation =>
            affiliation.source !== "relationship" &&
            affiliation.membershipStatus === "active"
        ).length > 1
    }


    // Default scope dashboard pages start with; per-page chips can diverge from it locally.
    get defaultAllCompaniesScopeActive(): boolean {
        return this._storedDefaultAllCompaniesScopeActive()
    }


    activateDefaultAllCompaniesScope() {
        this._changeDefaultAllCompaniesScope(YES)
    }


    deactivateDefaultAllCompaniesScope() {
        this._changeDefaultAllCompaniesScope(NO)
    }


    _changeDefaultAllCompaniesScope(active: boolean) {
        if (this.defaultAllCompaniesScopeActive === active) {
            return
        }
        this._storeDefaultAllCompaniesScopeActive(active)
        this._notifySubscribers("defaultAllCompaniesScope")
        this._broadcastDefaultAllCompaniesScopeDidChange()
    }


    async retrieveMyCompanyAffiliations(reloads: boolean = NO): Promise<RHCompanyAffiliation[]> {
        // The authenticated welcome message assigns userProfile before CBCore
        // flips isUserLoggedIn and broadcasts userDidLogIn. userProfile is the
        // session identity that makes this pre-broadcast hydration safe.
        if (!this._core.userProfile) {
            this.clear()
            return []
        }

        if (IS(this._affiliationsPromise)) {
            if (reloads === YES) {
                this._reloadRequestedWhileLoading = YES
            }
            return this._affiliationsPromise
        }

        if (reloads !== YES && this._affiliationLoadState === "loaded") {
            return this.affiliations
        }

        const loadGeneration = ++this._loadGeneration
        const userId = this._currentUserId()
        this._affiliationLoadState = "loading"
        const affiliationsPromise = this._retrieveMyCompanyAffiliationsUntilCurrent(
            loadGeneration,
            userId
        )
        this._affiliationsPromise = affiliationsPromise

        try {
            const affiliations = await affiliationsPromise
            if (this._loadRequestIsCurrent(loadGeneration, userId)) {
                this._affiliationLoadState = "loaded"
            }
            return affiliations
        }
        catch (error) {
            if (this._loadRequestIsCurrent(loadGeneration, userId)) {
                this._affiliationLoadState = "failed"
            }
            throw error
        }
        finally {
            if (this._affiliationsPromise === affiliationsPromise) {
                this._affiliationsPromise = undefined
            }
        }
    }


    async retrieveMyCompanyMembershipsAndRequests(
        reloads: boolean = NO
    ): Promise<{
        memberships: RHCompanyAffiliation[]
        workflows: RHCompanyMembershipWorkflow[]
    }> {
        await this.retrieveMyCompanyAffiliations(reloads)
        return {
            memberships: this.affiliations,
            workflows: this.membershipWorkflows
        }
    }


    activateCompany(companyId: string): boolean {
        const affiliation = this._affiliations.find(candidate =>
            companyIdForAffiliation(candidate) === companyId
        )
        if (
            !affiliation ||
            affiliation.source === "relationship" ||
            affiliation.membershipStatus !== "active"
        ) {
            return NO
        }
        if (this._activeCompanyId === companyId) {
            return YES
        }

        this._activeCompanyId = companyId
        this._storeActiveCompanyId(companyId)
        this.invalidateCompanyScopedCaches()
        this._notifySubscribers("activeCompany")
        this._broadcastActiveCompanyDidChange()
        return YES
    }


    async activeCompanyIdAfterLoadingAffiliations(): Promise<string | undefined> {
        await this.retrieveMyCompanyAffiliations()
        return this.activeCompanyId
    }


    clear() {
        this._loadGeneration += 1
        this._affiliations = []
        this._membershipWorkflows = []
        this._affiliationsPromise = undefined
        this._activeCompanyId = undefined
        this._directCompanyAffiliation = undefined
        this._affiliationLoadState = "notLoaded"
        this._reloadRequestedWhileLoading = NO
        this._notifySubscribers("affiliations")
        this._broadcastAffiliationsDidChange()
    }


    invalidateCompanyScopedCaches() {
        this._core.offererConfigurationStore.invalidateTenderInterestSettings()
        this._core.notificationStore.activeCompanyDidChange()
        this._core.procurementStore.invalidateCompanyScopedCaches()
        this._core.offerStore.invalidateIncomingOffers()
        this._core.offerStore.invalidateOutgoingOffers()
        this._core.contractStore.invalidateCompanyScopedCaches()
        this._core.privateMessageStore.clear()
        this._core.notificationStore.clearOfferMessageCounts()
        this._core.notificationStore.refreshGlobalCounts()
    }


    _applicationModeStorageKey(): string {
        const userId = this._currentUserId()
        const companyId = this._activeCompanyId
        if (userId.length > 0 && companyId && companyId.length > 0) {
            return `RHCore_applicationMode_${userId}_${companyId}`
        }
        return "RHCore_applicationMode"
    }


    async _retrieveMyCompanyAffiliationsUntilCurrent(
        loadGeneration: number,
        userId: string
    ): Promise<RHCompanyAffiliation[]> {
        let affiliations: RHCompanyAffiliation[] = []
        do {
            this._reloadRequestedWhileLoading = NO
            affiliations = await this._retrieveMyCompanyAffiliations(loadGeneration, userId)
        }
        while (
            this._reloadWasRequestedWhileLoading() &&
            this._loadRequestIsCurrent(loadGeneration, userId)
        )
        return affiliations
    }


    _reloadWasRequestedWhileLoading(): boolean {
        return this._reloadRequestedWhileLoading
    }


    async _retrieveMyCompanyAffiliations(
        loadGeneration: number,
        userId: string
    ): Promise<RHCompanyAffiliation[]> {
        let unifiedEndpointError: unknown

        if (RHFeatureFlags.multiCompanyAffiliationIsEnabled === YES) {
            try {
                const response = await SocketClient.ListMyCompanyMembershipsAndRequests(
                    undefined,
                    CBSocketClient.completionPolicy.first
                )
                RHCompanyAffiliationStore._throwForSocketError(
                    response,
                    "Company memberships could not be loaded."
                )
                const affiliations = response.result?.memberships
                if (!Array.isArray(affiliations)) {
                    throw new Error("Company memberships response was incomplete.")
                }
                if (!this._loadRequestIsCurrent(loadGeneration, userId)) {
                    return this.affiliations
                }

                this._membershipWorkflows = response.result?.workflows ?? []
                this._directCompanyAffiliation = affiliations.find(affiliation =>
                    affiliation.source !== "relationship" &&
                    affiliation.membershipStatus === "active"
                )
                this._replaceAffiliations(affiliations)
                this._notifySubscribers("membershipWorkflows")
                return this.affiliations
            }
            catch (error) {
                if (!this._loadRequestIsCurrent(loadGeneration, userId)) {
                    return this.affiliations
                }
                unifiedEndpointError = error
                console.error(
                    "RHCompanyAffiliationStore: unified company-affiliation retrieval failed",
                    error
                )
            }
        }

        try {
            const legacyResult = await this._legacyCompanyAffiliationsForCurrentUser()
            if (!this._loadRequestIsCurrent(loadGeneration, userId)) {
                return this.affiliations
            }
            this._directCompanyAffiliation = legacyResult.directCompanyAffiliation
            this._membershipWorkflows = []
            this._replaceAffiliations(legacyResult.affiliations)
            return this.affiliations
        }
        catch (error) {
            console.error(
                "RHCompanyAffiliationStore: legacy company-affiliation retrieval failed",
                error
            )
            if (IS(unifiedEndpointError)) {
                console.error(
                    "RHCompanyAffiliationStore: both company-affiliation retrieval paths failed",
                    unifiedEndpointError
                )
            }
            throw error
        }
    }


    async _legacyCompanyAffiliationsForCurrentUser(): Promise<{
        affiliations: RHCompanyAffiliation[]
        directCompanyAffiliation: RHCompanyAffiliation | undefined
    }> {
        const response = await SocketClient.RetrieveDirectCompanyAffiliationForCurrentUser()
        RHCompanyAffiliationStore._throwForSocketError(
            response,
            "Current company affiliation could not be loaded."
        )
        const directCompanyAffiliation = response.result

        if (!directCompanyAffiliation?.company) {
            return {
                affiliations: [],
                directCompanyAffiliation
            }
        }

        const companyId = referenceId(directCompanyAffiliation.company._id)
        if (!companyId) {
            return {
                affiliations: [],
                directCompanyAffiliation
            }
        }

        return {
            affiliations: [{
                ...directCompanyAffiliation,
                company: {
                    _id: companyId,
                    businessRegistryCode: directCompanyAffiliation.company.businessRegistryCode,
                    name: directCompanyAffiliation.company.name
                },
                roles: RHCompanyAffiliationStore._rolesForAffiliation(directCompanyAffiliation),
                membershipStatus: "active",
                source: "legacy-backfill"
            }],
            directCompanyAffiliation
        }
    }


    _replaceAffiliations(affiliations: RHCompanyAffiliation[]) {
        const previousActiveCompanyId = this._activeCompanyId
        this.deactivateDefaultAllCompaniesScope()
        this._affiliations = affiliations
        this._activeCompanyId = this._selectedActiveCompanyIdForAffiliations(affiliations)

        if (this._activeCompanyId) {
            this._storeActiveCompanyId(this._activeCompanyId)
        }

        this._notifySubscribers("affiliations")
        this._broadcastAffiliationsDidChange()

        // Cross-company lists and search depend on every membership, including
        // changes that leave the selected company unchanged.
        this.invalidateCompanyScopedCaches()
        if (previousActiveCompanyId !== this._activeCompanyId) {
            this._notifySubscribers("activeCompany")
            this._broadcastActiveCompanyDidChange()
        }
    }


    _selectedActiveCompanyIdForAffiliations(affiliations: RHCompanyAffiliation[]): string | undefined {
        const directActiveAffiliations = affiliations.filter(affiliation =>
            affiliation.source !== "relationship" &&
            affiliation.membershipStatus === "active"
        )
        const storedCompanyId = this._storedActiveCompanyId()
        if (
            storedCompanyId &&
            this._affiliationsContainCompanyId(directActiveAffiliations, storedCompanyId)
        ) {
            return storedCompanyId
        }

        if (
            this._activeCompanyId &&
            this._affiliationsContainCompanyId(directActiveAffiliations, this._activeCompanyId)
        ) {
            return this._activeCompanyId
        }

        return companyIdForAffiliation(directActiveAffiliations[0])
    }


    _affiliationsContainCompanyId(affiliations: RHCompanyAffiliation[], companyId: string): boolean {
        return affiliations.some(affiliation => companyIdForAffiliation(affiliation) === companyId)
    }


    _storedActiveCompanyId(): string | undefined {
        const storageKey = this._activeCompanyStorageKey()
        if (!storageKey) {
            return undefined
        }
        return localStorage.getItem(storageKey) ?? undefined
    }


    _storeActiveCompanyId(companyId: string) {
        const storageKey = this._activeCompanyStorageKey()
        if (!storageKey) {
            return
        }
        localStorage.setItem(storageKey, companyId)
    }


    _activeCompanyStorageKey(): string | undefined {
        const userId = this._currentUserId()
        if (userId.length === 0) {
            return undefined
        }
        return `RHCore_activeCompanyId_${userId}`
    }


    _storedDefaultAllCompaniesScopeActive(): boolean {
        const storageKey = this._defaultAllCompaniesScopeStorageKey()
        if (!storageKey) {
            return NO
        }
        return localStorage.getItem(storageKey) === "1"
    }


    _storeDefaultAllCompaniesScopeActive(active: boolean) {
        const storageKey = this._defaultAllCompaniesScopeStorageKey()
        if (!storageKey) {
            return
        }
        if (active) {
            localStorage.setItem(storageKey, "1")
        }
        else {
            localStorage.setItem(storageKey, "0")
        }
    }


    _defaultAllCompaniesScopeStorageKey(): string | undefined {
        const userId = this._currentUserId()
        if (userId.length === 0) {
            return undefined
        }
        return `RHCore_defaultAllCompaniesScope_${userId}`
    }


    _currentUserId(): string {
        return this._core.userProfile?._id ?? ""
    }


    _loadRequestIsCurrent(loadGeneration: number, userId: string): boolean {
        return loadGeneration === this._loadGeneration &&
            userId.length > 0 &&
            userId === this._currentUserId()
    }


    _registerSocketListeners() {
        const socketClient = this._core.socketClient

        socketClient.addTargetForMessagesForKey(
            "CompanyAffiliationDidChange",
            () => this._refreshAffiliationsAfterPush()
        )
        socketClient.addTargetForMessagesForKey(
            "CompanyRelationshipDidChange",
            () => this._refreshAffiliationsAfterPush()
        )
        socketClient.addTargetForMessagesForKey(
            "CompanyMembershipDidChange",
            (payload: { userIds?: string[] }) => {
                const currentUserId = this._currentUserId()
                if (
                    !payload?.userIds ||
                    payload.userIds.length === 0 ||
                    payload.userIds.includes(currentUserId)
                ) {
                    this._refreshAffiliationsAfterPush()
                }
            }
        )
    }


    _refreshAffiliationsAfterPush() {
        this._pushRefreshRequested = YES
        if (IS(this._pushRefreshPromise)) {
            return
        }

        this._pushRefreshPromise = Promise.resolve()
            .then(async () => {
                while (this._pushRefreshRequested === YES) {
                    this._pushRefreshRequested = NO
                    await this._core.refreshCurrentCompanyAffiliation()
                }
            })
            .catch(error => {
                console.error(
                    "RHCompanyAffiliationStore: failed to refresh company affiliation after push",
                    error
                )
            })
            .finally(() => {
                this._pushRefreshPromise = undefined
            })
    }


    _broadcastAffiliationsDidChange() {
        this._broadcastEventSafely(RHCore.broadcastEventName.companyAffiliationsDidChange)
    }


    _broadcastActiveCompanyDidChange() {
        this._broadcastEventSafely(RHCore.broadcastEventName.activeCompanyDidChange)
        this._broadcastEventSafely(RHCore.broadcastEventName.applicationModeDidChange)
    }


    _broadcastDefaultAllCompaniesScopeDidChange() {
        this._broadcastEventSafely(
            RHCore.broadcastEventName.defaultAllCompaniesScopeDidChange
        )
    }


    _broadcastEventSafely(eventName: string) {
        try {
            this._core.broadcastMessageInRootViewTree({
                name: eventName
            } as UIViewBroadcastEvent)
        }
        catch (error) {
            // A view-level event handler must never turn an already successful
            // server response into an empty company snapshot.
            console.error(
                "RHCompanyAffiliationStore: company-affiliation event handler threw",
                error
            )
        }
    }


    static _rolesForAffiliation(affiliation: RHCompanyAffiliation): RHCompanyRole[] {
        const roles: RHCompanyRole[] = []
        if (affiliation.isAdministrator === YES) {
            roles.push("administrator")
        }
        if (affiliation.isProcurer === YES) {
            roles.push("procurer")
        }
        if (affiliation.isAuditor === YES) {
            roles.push("auditor")
        }
        if (affiliation.isConsultant === YES) {
            roles.push("consultant")
        }
        if (affiliation.isOfferer === YES) {
            roles.push("offerer")
        }
        return roles
    }


    static _throwForSocketError(
        response: { errorResult?: unknown },
        fallbackMessage: string
    ) {
        if (!IS(response.errorResult)) {
            return
        }

        let message = fallbackMessage
        if (typeof response.errorResult === "object") {
            const messageData = (response.errorResult as { messageData?: unknown }).messageData
            if (typeof messageData === "string" && messageData.length > 0) {
                message = messageData
            }
        }
        throw new Error(message)
    }

}
